import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Users, MoreVertical, Pencil, Power, Trash2, KeyRound, Loader2, Package, Boxes } from 'lucide-react';
import client, { apiErrorMessage, apiErrorDetails, API } from '@/lib/api';
import { auth as authStore } from '@/lib/api';
import { useQuery as useQueryReact } from '@tanstack/react-query';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { stripEmpty } from '@/lib/formUtils';
import PageHeader from '@/components/PageHeader';
import ListToolbar from '@/components/ListToolbar';
import Pagination from '@/components/PaginationBar';
import StatusBadge from '@/components/StatusBadge';
import EmptyState from '@/components/EmptyState';
import ShareCredentialsModal from '@/components/ShareCredentialsModal';
import { TableSkeleton } from '@/components/Skeletons';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { formatCurrency, formatDate } from '@/lib/format';

const EMPTY = {
  businessName: '', contactPerson: '', businessEmail: '', businessPhone: '',
  businessCategory: '', address: '', perCartonPerDayRate: '',
  gstNumber: '', panNumber: '', city: '', state: '', country: 'India', pincode: '',
};

export default function CustomersPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [addStockFor, setAddStockFor] = useState(null);

  const q = useQuery({
    queryKey: ['customers', { page, search, status }],
    queryFn: () => client.get('/customers', { params: { page, limit: 20, search: search || undefined, status: status || undefined } }).then((r) => r.data),
  });
  const items = q.data?.data || [];
  const meta = q.data?.meta?.pagination || { page: 1, totalPages: 1 };

  async function toggleStatus(c) {
    try {
      await client.post(`/customers/${c.id}/${c.status === 'ACTIVE' ? 'disable' : 'enable'}`);
      toast.success(`Customer ${c.status === 'ACTIVE' ? 'disabled' : 'enabled'}`);
      qc.invalidateQueries({ queryKey: ['customers'] });
    } catch (err) { toast.error(apiErrorMessage(err)); }
  }
  async function del(c) {
    try {
      await client.delete(`/customers/${c.id}`);
      toast.success('Customer deleted');
      qc.invalidateQueries({ queryKey: ['customers'] });
    } catch (err) { toast.error(apiErrorMessage(err)); }
  }
  async function resetPwd(c) {
    try {
      const res = await client.post(`/customers/${c.id}/reset-password`);
      setShareData({
        title: 'Password reset',
        businessName: c.businessName,
        credentials: res.data.data.credentials,
        whatsappShareUrl: res.data.data.whatsappShareUrl,
      });
    } catch (err) { toast.error(apiErrorMessage(err)); }
  }

  return (
    <div data-testid="customers-page">
      <PageHeader
        title="Customers"
        description="Businesses whose inventory you store & bill."
        actions={<Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-zinc-950 hover:bg-zinc-800 text-white h-10" data-testid="cust-create-button"><Plus className="w-4 h-4 mr-1.5" /> New customer</Button>}
      />
      <ListToolbar
        search={search} onSearchChange={(v) => { setSearch(v); setPage(1); }}
        statusValue={status} statusOptions={['ACTIVE', 'DISABLED']}
        onStatusChange={(v) => { setStatus(v); setPage(1); }}
        placeholder="Search business, contact, email, phone…"
        testIdPrefix="cust"
      />

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        {q.isLoading ? <TableSkeleton cols={6} /> : items.length === 0 ? (
          <EmptyState icon={Users} title="No customers yet" description="Add a customer to start managing their inventory & billing." />
        ) : (
          <table className="w-full text-sm" data-testid="customers-table">
            <thead>
              <tr className="bg-zinc-50 text-zinc-500 text-[11px] uppercase tracking-wider">
                <th className="text-left px-4 py-3">Business</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Phone</th>
                <th className="text-right px-4 py-3">Rate / carton / day</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Since</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {items.map((c) => (
                <tr key={c.id} className="hover:bg-zinc-50" data-testid={`cust-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-zinc-950">{c.businessName}</div>
                    {c.businessCategory && <div className="text-xs text-zinc-500">{c.businessCategory}</div>}
                  </td>
                  <td className="px-4 py-3 text-zinc-700">{c.contactPerson}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-700">{c.businessEmail}</td>
                  <td className="px-4 py-3 font-mono text-xs">{c.businessPhone}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatCurrency(c.perCartonPerDayRate)}</td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{formatDate(c.createdAt)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setAddStockFor(c)}
                        title="Add stock for this customer"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 transition-colors"
                        data-testid={`cust-add-stock-btn-${c.id}`}
                      >
                        <Plus className="w-3.5 h-3.5" /> Add stock
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><button className="p-1.5 hover:bg-zinc-100 rounded" data-testid={`cust-actions-${c.id}`}><MoreVertical className="w-4 h-4 text-zinc-500" /></button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuItem onClick={() => setAddStockFor(c)} data-testid={`cust-add-stock-${c.id}`}><Boxes className="w-4 h-4 mr-2 text-emerald-700" /> Add stock</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => { setEditing(c); setFormOpen(true); }} data-testid={`cust-edit-${c.id}`}><Pencil className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleStatus(c)} data-testid={`cust-toggle-${c.id}`}><Power className="w-4 h-4 mr-2" /> {c.status === 'ACTIVE' ? 'Disable' : 'Enable'}</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => resetPwd(c)} data-testid={`cust-reset-${c.id}`}><KeyRound className="w-4 h-4 mr-2" /> Reset password</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <DropdownMenuItem className="text-red-600 focus:text-red-700 focus:bg-red-50" onSelect={(e) => e.preventDefault()} data-testid={`cust-delete-${c.id}`}><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Delete "{c.businessName}"?</AlertDialogTitle>
                              <AlertDialogDescription>This soft-deletes the customer and disables their login.</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => del(c)} data-testid={`cust-delete-confirm-${c.id}`}>Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Pagination page={meta.page} totalPages={meta.totalPages} onChange={setPage} />
      </div>

      <CustomerFormDialog
        open={formOpen} onOpenChange={setFormOpen} initial={editing}
        onSaved={(data) => {
          setFormOpen(false);
          qc.invalidateQueries({ queryKey: ['customers'] });
          if (!editing && data?.credentials) {
            setShareData({ title: 'Customer created', businessName: data.customer?.businessName, credentials: data.credentials, whatsappShareUrl: data.whatsappShareUrl });
          }
        }}
      />
      <ShareCredentialsModal open={!!shareData} onClose={() => setShareData(null)} title={shareData?.title} businessName={shareData?.businessName} credentials={shareData?.credentials} whatsappShareUrl={shareData?.whatsappShareUrl} />
      <AddStockForCustomerDialog customer={addStockFor} onClose={() => setAddStockFor(null)} onSaved={() => { setAddStockFor(null); qc.invalidateQueries({ queryKey: ['inventory'] }); toast.success('Stock added'); }} />
    </div>
  );
}

function AddStockForCustomerDialog({ customer, onClose, onSaved }) {
  const [productMasterId, setProductMasterId] = useState('');
  const [productName, setProductName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [batchNumber, setBatchNumber] = useState('');
  const [cartonQuantity, setCartonQuantity] = useState("");
  const [unit, setUnit] = useState("Cartons");
  const [inDate, setInDate] = useState('');
  const [loading, setLoading] = useState(false);
  const open = !!customer;

  const productsQ = useQueryReact({
    queryKey: ['products-lite'],
    queryFn: () => client.get('/products', { params: { limit: 200 } }).then((r) => r.data.data || []),
    enabled: open, staleTime: 60_000,
  });
  const products = productsQ.data || [];

  useEffect(() => {
    if (open) {
      setProductMasterId(''); setProductName(''); setCompanyName('');
      setBatchNumber(''); setCartonQuantity(''); setInDate('');
    }
  }, [open]);

  function onProductSelect(id) {
    const p = products.find((x) => x.id === id);
    setProductMasterId(id);
    setProductName(p?.name || '');
    setCompanyName(p?.companyName || '');
  }

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await client.post('/inventory', {
        productName, companyName, batchNumber,
        cartonQuantity: parseInt(cartonQuantity, 10),
        inDate: inDate ? new Date(inDate).toISOString() : undefined,
        customerId: customer.id,
      });
      onSaved();
    } catch (err) { toast.error(apiErrorMessage(err)); }
    finally { setLoading(false); }
  }

    const selectedProduct = products.find((p) => p.id === productMasterId);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl p-0 overflow-hidden border-zinc-200 shadow-2xl rounded-2xl" data-testid="cust-add-stock-dialog">
        {/* Header with visual accent */}
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 text-white p-6 pb-5">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center border border-white/15 backdrop-blur-md">
                <Boxes className="w-5 h-5 text-zinc-100" />
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold tracking-tight text-white">Add Inbound Stock</DialogTitle>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-zinc-400">Customer:</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white/15 text-zinc-100 border border-white/10">
                    {customer?.businessName || "Unknown"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5 bg-white">
          {/* Section 1: Product Selection */}
          <div className="rounded-xl border border-zinc-200/80 bg-zinc-50/50 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Product Master</Label>
              {selectedProduct?.sku && (
                <span className="text-[11px] font-mono bg-zinc-200/70 text-zinc-700 px-2 py-0.5 rounded">
                  SKU: {selectedProduct.sku}
                </span>
              )}
            </div>

            <div>
              <Select value={productMasterId || undefined} onValueChange={onProductSelect}>
                <SelectTrigger className="bg-white border-zinc-300 hover:border-zinc-400 shadow-sm transition-colors text-sm" data-testid="cust-add-stock-product-select">
                  <SelectValue placeholder={productsQ.isLoading ? "Loading master catalog…" : (products.length === 0 ? "No products in master catalog" : "Search or choose a product…")} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`cust-add-stock-opt-${p.id}`}>
                      <div className="flex items-center justify-between gap-4">
                        <span className="font-medium text-zinc-900">{p.name}</span>
                        {p.companyName && <span className="text-xs text-zinc-500 font-normal">({p.companyName})</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Read-only Company Name */}
            <div>
              <Label className="text-xs font-medium text-zinc-500">Company / Brand Name</Label>
              <div className="mt-1 flex items-center justify-between px-3 py-2 rounded-lg bg-zinc-100/90 border border-zinc-200 text-sm font-medium text-zinc-800 select-none">
                <span>{companyName || "Select a product above"}</span>
                <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-400">Auto-filled</span>
              </div>
            </div>
          </div>

          {/* Section 2: Lot & Inward Details */}
          <div className="rounded-xl border border-zinc-200/80 bg-zinc-50/50 p-4 space-y-3">
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Lot & Quantity Details</Label>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-zinc-700">Batch / Lot Number *</Label>
                <Input
                  value={batchNumber}
                  onChange={(e) => setBatchNumber(e.target.value)}
                  placeholder="e.g. BATCH-2026-A"
                  required
                  className="mt-1 font-mono text-sm bg-white border-zinc-300 shadow-sm uppercase tracking-wide"
                  data-testid="cust-add-stock-batch"
                />
              </div>

              <div>
                <Label className="text-xs font-medium text-zinc-700">Inward Date *</Label>
                <Input
                  type="date"
                  value={inDate}
                  onChange={(e) => setInDate(e.target.value)}
                  required
                  className="mt-1 bg-white border-zinc-300 text-sm shadow-sm text-zinc-700"
                  data-testid="cust-add-stock-indate"
                />
              </div>

              {/* Quantity with Custom Unit Picker */}
              <div className="sm:col-span-2">
                <Label className="text-xs font-medium text-zinc-700">Stock Quantity & Unit *</Label>
                <div className="mt-1 flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      type="number"
                      min="1"
                      value={cartonQuantity}
                      onChange={(e) => setCartonQuantity(e.target.value)}
                      placeholder="Enter quantity"
                      required
                      className="font-mono text-base font-semibold bg-white border-zinc-300 shadow-sm"
                      data-testid="cust-add-stock-qty"
                    />
                  </div>

                  <div className="w-36">
                    <Select value={unit} onValueChange={setUnit}>
                      <SelectTrigger className="bg-white border-zinc-300 font-medium text-zinc-800 shadow-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Cartons">Cartons</SelectItem>
                        <SelectItem value="Boxes">Boxes</SelectItem>
                        <SelectItem value="Pieces">Pieces</SelectItem>
                        <SelectItem value="Packets">Packets</SelectItem>
                        <SelectItem value="Bags">Bags</SelectItem>
                        <SelectItem value="Pallets">Pallets</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-zinc-300 text-zinc-700 hover:bg-zinc-100 px-4"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !productName}
              className="bg-zinc-900 hover:bg-zinc-800 text-white shadow-md font-medium px-5 transition-all"
              data-testid="cust-add-stock-submit"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Adding Stock…</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  <span>Add Stock</span>
                </div>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CustomerFormDialog({ open, onOpenChange, initial, onSaved }) {
  const categoriesQ = useQueryReact({
    queryKey: ["category-masters"],
    queryFn: () => client.get("/category-masters").then((r) => r.data.data || []),
    enabled: open,
    staleTime: 60_000,
  });
  const categoryList = categoriesQ.data || [];
  const isEdit = !!initial;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          ...EMPTY,
          ...initial,
          ...(initial.profile || {}),
          perCartonPerDayRate: initial.perCartonPerDayRate || '',
        });
      } else setForm(EMPTY);
      setErrors({});
    }
  }, [open, initial]);

  function update(patch) { setForm((f) => ({ ...f, ...patch })); }

  async function submit(e) {
    e.preventDefault();
    setLoading(true); setErrors({});
    try {
      const payload = stripEmpty({ ...form, perCartonPerDayRate: parseFloat(form.perCartonPerDayRate) });
      const res = isEdit
        ? await client.patch(`/customers/${initial.id}`, payload)
        : await client.post('/customers', payload);
      toast.success(isEdit ? 'Customer updated' : 'Customer created');
      onSaved(res.data.data);
    } catch (err) {
      const details = apiErrorDetails(err);
      if (details && Array.isArray(details)) {
        const map = {}; for (const d of details) map[d.field.replace('body.','')] = d.message;
        setErrors(map);
      }
      toast.error(apiErrorMessage(err));
    } finally { setLoading(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="customer-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? 'Edit customer' : 'New customer'}</DialogTitle>
          <DialogDescription className="text-zinc-500">Business details, contact & GST info, and per-carton-per-day billing rate.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <SectionHeading>Business</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Fld label="Business name*" err={errors.businessName}><Input value={form.businessName} onChange={(e) => update({ businessName: e.target.value })} required data-testid="cust-form-name" /></Fld>
            <Fld label="Contact person*" err={errors.contactPerson}><Input value={form.contactPerson} onChange={(e) => update({ contactPerson: e.target.value })} required data-testid="cust-form-contact" /></Fld>
            <Fld label="Business email*" err={errors.businessEmail}><Input type="email" value={form.businessEmail} onChange={(e) => update({ businessEmail: e.target.value })} required disabled={isEdit} data-testid="cust-form-email" /></Fld>
            <Fld label="Business phone*" err={errors.businessPhone}><Input value={form.businessPhone} onChange={(e) => update({ businessPhone: e.target.value })} required data-testid="cust-form-phone" /></Fld>
            <Fld label="Category">
              <Select value={form.businessCategory || "none"} onValueChange={(val) => update({ businessCategory: val === "none" ? "" : val })}>
                <SelectTrigger className="h-9 text-xs border-zinc-200" data-testid="cust-form-category">
                  <SelectValue placeholder={categoriesQ.isLoading ? "Loading..." : "Select a category..."} />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="none" className="text-zinc-400">None / Clear</SelectItem>
                  {categoryList.map((c) => (
                    <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Fld>
            <Fld label="Rate / carton / day*" err={errors.perCartonPerDayRate}>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-mono">₹</span>
                <Input type="number" step="0.01" min="0" value={form.perCartonPerDayRate} onChange={(e) => update({ perCartonPerDayRate: e.target.value })} required className="pl-7 font-mono" data-testid="cust-form-rate" />
              </div>
            </Fld>
          </div>
          <Fld label="Address*" err={errors.address}><Textarea value={form.address} onChange={(e) => update({ address: e.target.value })} required rows={2} data-testid="cust-form-address" /></Fld>

          <SectionHeading>GST & compliance</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Fld label="GST number"><Input value={form.gstNumber || ''} onChange={(e) => update({ gstNumber: e.target.value })} className="font-mono" data-testid="cust-form-gst" /></Fld>
            <Fld label="PAN"><Input value={form.panNumber || ''} onChange={(e) => update({ panNumber: e.target.value })} className="font-mono" data-testid="cust-form-pan" /></Fld>
            <Fld label="City"><Input value={form.city || ''} onChange={(e) => update({ city: e.target.value })} /></Fld>
            <Fld label="State"><Input value={form.state || ''} onChange={(e) => update({ state: e.target.value })} /></Fld>
            <Fld label="Country"><Input value={form.country || ''} onChange={(e) => update({ country: e.target.value })} /></Fld>
            <Fld label="Pincode"><Input value={form.pincode || ''} onChange={(e) => update({ pincode: e.target.value })} className="font-mono" /></Fld>
          </div>

          <DialogFooter className="border-t border-zinc-200 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="cust-form-cancel">Cancel</Button>
            <Button type="submit" disabled={loading} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="cust-form-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : isEdit ? 'Save changes' : 'Create customer'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const SectionHeading = ({ children }) => <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">{children}</div>;
function Fld({ label, err, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-zinc-600">{label}</Label>
      {children}
      {err && <div className="text-xs text-red-600">{err}</div>}
    </div>
  );
}
